import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import { CondReportCell, CondReportHeader } from "../components/CondReportCell";
import { useSort, SortTh } from "../components/SortableTh";
import QrCard from "../components/QrCard";
import { toast } from "sonner";
import {
  ArrowLeft, CircleNotch, Buildings, User, Phone, EnvelopeSimple, MapPin,
  Globe, IdentificationBadge, Receipt, ClipboardText, FileText, Briefcase,
  WarningCircle, MapTrifold, ArrowsClockwise, Database, Cube, ArrowSquareOut,
} from "@phosphor-icons/react";

const fmtSync = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "never");

const STATUS_COLORS = {
  NOT_STARTED: "#6B7280", IN_PROGRESS: "#0055FF", COMPLETED: "#059669",
  ARCHIVED: "#9CA3AF", INVOICED: "#7C3AED", CANCELLED: "#DC2626",
};
const statusColor = (s) => STATUS_COLORS[(s || "").toUpperCase()] || "#6B7280";

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid={`client-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-2 text-neutral-500">
        <Icon size={16} weight="bold" />
        <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <p className={`mt-2 font-heading text-3xl font-black tracking-tighter ${accent || "text-[#0A0A0A]"}`}>{value}</p>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon size={18} className="mt-0.5 shrink-0 text-neutral-400" />
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-400">{label}</p>
        <p className="text-sm font-medium text-[#0A0A0A]">{value}</p>
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [assets, setAssets] = useState(null);
  const quotesSort = useSort();
  const jobsSort = useSort();
  const assetsSort = useSort();

  const loadReport = (refresh = false) => {
    api.get(`/clients/${id}/report`, { params: { refresh } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => {
    setLoading(true);
    loadReport(false);
    setAssets(null);
    api.get(`/clients/${id}/assets`).then((r) => setAssets(r.data)).catch(() => setAssets({ assets: [], count: 0 }));
  }, [id]);

  const refresh = () => {
    setRefreshing(true);
    toast.promise(
      api.get(`/clients/${id}/report`, { params: { refresh: true } }).then((r) => setData(r.data)).finally(() => setRefreshing(false)),
      { loading: "Pulling latest from Aroflo…", success: "Client data refreshed from Aroflo.", error: "Refresh failed." }
    );
  };

  if (loading) {
    return <Layout><div className="flex h-64 items-center justify-center" data-testid="client-detail-loading"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;
  }
  if (error) {
    return <Layout><div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-600" data-testid="client-detail-error"><WarningCircle size={28} className="text-[#D97706]" />{error}</div></Layout>;
  }

  const c = data.client;
  const k = data.kpis;
  const addr = c.address || {};
  const contact = c.contact || {};
  const addrStr = [addr.addressLine, addr.suburb, addr.state, addr.postcode].filter(Boolean).join(", ");
  const gps = c.gpsCoordinate;

  return (
    <Layout>
      <div data-testid="client-detail-page">
        <button data-testid="back-to-clients" onClick={() => navigate("/clients")} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#0055FF]">
          <ArrowLeft size={16} weight="bold" /> All clients
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#0A0A0A] text-white">
              {c.individual ? <User size={24} weight="fill" /> : <Buildings size={24} weight="fill" />}
            </div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" data-testid="client-name">{c.name}</h1>
              <p className="text-sm text-neutral-500 font-mono">{c.id} · {c.shortName || (c.individual ? "Individual" : "Company")}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button
              data-testid="client-refresh-btn"
              onClick={refresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-sm border border-[#0055FF] px-3.5 py-2 text-sm font-semibold text-[#0055FF] transition-all duration-200 hover:bg-[#0055FF]/5 disabled:opacity-60"
            >
              <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} /> Refresh from Aroflo
            </button>
            <span className="flex items-center gap-1.5 text-xs text-neutral-400" data-testid="client-synced-at">
              <Database size={12} /> {data._source === "db" ? "From cache · " : "Synced · "}{fmtSync(data._cached_at)}
            </span>
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi icon={ClipboardText} label="Total Jobs" value={k.total_tasks?.toLocaleString()} />
          <Kpi icon={Briefcase} label="Open Jobs" value={k.open_tasks} accent="text-[#0055FF]" />
          <Kpi icon={FileText} label="Quotes" value={k.total_quotes} />
          <Kpi icon={Receipt} label="Invoices" value={k.total_invoices} />
        </div>

        <div className="mt-6">
          <QrCard
            testid="client-qr-card"
            apiPath={`/clients/${c.id}/qr`}
            targetUrl={`${window.location.origin}/clients/${c.id}`}
            downloadName={`client-${c.id}-qr.jpg`}
            title="Scan to open this client"
            description="Anyone who scans this code is taken to this client's summary page. They'll be asked to sign in first if they aren't already."
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Contact details — 1/3 */}
          <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
            <h3 className="mb-2 font-heading text-lg font-black tracking-tight">Client Details</h3>
            <div className="divide-y divide-neutral-100">
              <InfoRow icon={Phone} label="Phone" value={contact.phone || contact.mobile} />
              <InfoRow icon={EnvelopeSimple} label="Email" value={contact.email} />
              <InfoRow icon={MapPin} label="Address" value={addrStr} />
              <InfoRow icon={IdentificationBadge} label="ABN" value={c.abn} />
              <InfoRow icon={Receipt} label="Terms" value={c.transactionTerms?.terms} />
              <InfoRow icon={Globe} label="Website" value={c.website} />
            </div>
          </div>

          {/* Map — 1/3 */}
          <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
            <h3 className="mb-2 font-heading text-lg font-black tracking-tight">Location</h3>
            {gps?.latitude ? (
              <div data-testid="client-map-thumb">
                <a
                  href={`https://www.google.com/maps?q=${gps.latitude},${gps.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative block overflow-hidden rounded-sm border border-neutral-200"
                  data-testid="client-map-link"
                >
                  <iframe
                    title="Client location map"
                    className="pointer-events-none h-64 w-full"
                    loading="lazy"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${gps.longitude - 0.008}%2C${gps.latitude - 0.006}%2C${gps.longitude + 0.008}%2C${gps.latitude + 0.006}&layer=mapnik&marker=${gps.latitude}%2C${gps.longitude}`}
                  />
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-sm bg-[#0A0A0A]/85 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm transition-all duration-200 group-hover:bg-[#0055FF]">
                    <MapTrifold size={14} weight="fill" /> Open in Google Maps
                  </span>
                </a>
              </div>
            ) : (
              <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-neutral-300 text-sm text-neutral-400" data-testid="client-map-empty">
                <MapTrifold size={24} /> No location on file
              </div>
            )}
          </div>

          {/* Instructions — 1/3 */}
          <div className="border border-[#D97706]/30 bg-[#D97706]/5 p-5 shadow-sm rounded-sm" data-testid="client-instructions">
            <h3 className="mb-2 font-heading text-lg font-black tracking-tight">Instructions</h3>
            {c.message ? (
              <p className="whitespace-pre-line text-sm text-neutral-700">{c.message}</p>
            ) : (
              <p className="text-sm text-neutral-400">No instructions on file.</p>
            )}
          </div>
        </div>

        {/* Quotes */}
        {data.quotes.length > 0 && (
          <div className="mt-6 border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="client-quotes">
            <div className="border-b border-neutral-200 px-5 py-3"><h3 className="font-heading text-lg font-black tracking-tight">Quotes ({k.total_quotes})</h3></div>
            <div className="max-h-80 overflow-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-neutral-50"><tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                  <SortTh label="Quote / Job #" field="ref" sort={quotesSort.sort} onSort={quotesSort.toggle} />
                  <SortTh label="Description" field="name" sort={quotesSort.sort} onSort={quotesSort.toggle} />
                  <SortTh label="Status" field="status" sort={quotesSort.sort} onSort={quotesSort.toggle} />
                </tr></thead>
                <tbody>
                  {quotesSort.sortRows(data.quotes, { ref: (q) => q.jobNumber || q.id }).map((q) => (
                    <tr key={q.id} className="border-b border-neutral-100 even:bg-neutral-50/60">
                      <td className="px-4 py-2.5">
                        <button
                          data-testid={`quote-link-${q.jobNumber || q.id}`}
                          onClick={() => navigate(`/quotes/${q.id}`)}
                          className="font-mono font-semibold text-[#0055FF] hover:underline"
                        >
                          {q.jobNumber || q.id}
                        </button>
                      </td>
                      <td className="max-w-[320px] truncate px-4 py-2.5">{q.name || "—"}</td>
                      <td className="px-4 py-2.5"><span className="rounded-sm px-2 py-0.5 text-xs font-semibold" style={{ background: statusColor(q.status) + "1a", color: statusColor(q.status) }}>{q.status || "—"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent Jobs — moved below Quotes */}
        <div className="mt-6 border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="client-jobs-section">
          <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
            <h3 className="font-heading text-lg font-black tracking-tight">Recent Jobs</h3>
            <div className="flex flex-wrap gap-1.5" data-testid="status-breakdown">
              {data.task_status_breakdown.map((s) => (
                <span key={s.status} className="rounded-sm px-2 py-0.5 text-xs font-semibold" style={{ background: statusColor(s.status) + "1a", color: statusColor(s.status) }}>
                  {s.status} {s.count}
                </span>
              ))}
            </div>
          </div>
          {data.tasks.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-sm text-neutral-500" data-testid="no-jobs">No jobs found for this client.</div>
          ) : (
            <div className="max-h-[460px] overflow-auto" data-testid="client-jobs-table">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-neutral-50">
                  <tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                    <SortTh label="Job #" field="jobNumber" sort={jobsSort.sort} onSort={jobsSort.toggle} />
                    <SortTh label="Description" field="name" sort={jobsSort.sort} onSort={jobsSort.toggle} />
                    <SortTh label="Type" field="taskType" sort={jobsSort.sort} onSort={jobsSort.toggle} />
                    <SortTh label="Site" field="site" sort={jobsSort.sort} onSort={jobsSort.toggle} />
                    <SortTh label="Due" field="dueDate" sort={jobsSort.sort} onSort={jobsSort.toggle} />
                    <SortTh label="Status" field="status" sort={jobsSort.sort} onSort={jobsSort.toggle} />
                  </tr>
                </thead>
                <tbody>
                  {jobsSort.sortRows(data.tasks).map((t) => (
                    <tr key={t.id} className="border-b border-neutral-100 even:bg-neutral-50/60" data-testid={`job-row-${t.jobNumber}`}>
                      <td className="px-4 py-2.5">
                        <button
                          data-testid={`job-link-${t.jobNumber}`}
                          onClick={() => navigate(`/jobs/${t.id}`)}
                          className="font-mono font-semibold text-[#0055FF] hover:underline"
                        >
                          {t.jobNumber}
                        </button>
                      </td>
                      <td className="max-w-[240px] truncate px-4 py-2.5" title={t.name}>{t.name || "—"}</td>
                      <td className="px-4 py-2.5 text-neutral-600">{t.taskType || "—"}</td>
                      <td className="px-4 py-2.5 text-neutral-600">{t.site || "—"}</td>
                      <td className="px-4 py-2.5 font-mono text-neutral-600">{t.dueDate || "—"}</td>
                      <td className="px-4 py-2.5">
                        <span className="rounded-sm px-2 py-0.5 text-xs font-semibold" style={{ background: statusColor(t.status) + "1a", color: statusColor(t.status) }}>{t.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-4 text-xs text-neutral-400" data-testid="data-sources-note">
          Stored in the Hub database (Clients, Tasks, Quotes{k.total_invoices ? ", Invoices" : ""}) and refreshable from Aroflo. Showing up to 50 most-recent records per feed.
        </p>

        {/* Related assets */}
        <div className="mt-8" data-testid="client-assets-section">
          <div className="flex items-center gap-2">
            <Cube size={20} weight="bold" className="text-[#0055FF]" />
            <h2 className="font-heading text-xl font-black tracking-tight text-[#0A0A0A]">Related Assets</h2>
            {assets && <span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-600" data-testid="client-assets-count">{assets.count}</span>}
          </div>
          {assets === null ? (
            <div className="mt-3 flex h-24 items-center justify-center rounded-sm border border-neutral-200 bg-white"><CircleNotch size={22} className="animate-spin text-[#0055FF]" /></div>
          ) : assets.count === 0 ? (
            <div className="mt-3 rounded-sm border border-dashed border-neutral-300 bg-white p-6 text-center" data-testid="client-assets-empty">
              <p className="text-sm text-neutral-500">No assets found for this client in the asset register.</p>
            </div>
          ) : (
            <div className="mt-3 overflow-x-auto border border-neutral-200 bg-white shadow-sm rounded-sm">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                    <SortTh label="Asset name" field="asset_name" sort={assetsSort.sort} onSort={assetsSort.toggle} />
                    <SortTh label="Asset type" field="category_name" sort={assetsSort.sort} onSort={assetsSort.toggle} />
                    <SortTh label="Business unit" field="org_name" sort={assetsSort.sort} onSort={assetsSort.toggle} />
                    <SortTh label="Barcode" field="barcode" sort={assetsSort.sort} onSort={assetsSort.toggle} />
                    <SortTh label="Serial" field="serial_number" sort={assetsSort.sort} onSort={assetsSort.toggle} />
                    <SortTh label={<CondReportHeader />} field="cond_report" sort={assetsSort.sort} onSort={assetsSort.toggle} align="center" />
                    <th className="px-4 py-2.5 text-right" />
                  </tr>
                </thead>
                <tbody>
                  {assetsSort.sortRows(assets.assets, { cond_report: (a) => (a.has_condition_report ? 1 : 0) + (a.has_condition_values ? 1 : 0) }).map((a) => (
                    <tr key={a.asset_id} data-testid={`client-asset-row-${a.asset_id}`}
                      onClick={() => window.open(`/asset-detail?id=${encodeURIComponent(a.asset_id)}`, "_blank", "noopener")}
                      className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50/60 hover:bg-[#0055FF]/[0.03]">
                      <td className="max-w-[280px] truncate px-4 py-2.5 font-semibold text-[#0A0A0A]" title={a.asset_name}>{a.asset_name || "—"}</td>
                      <td className="max-w-[180px] truncate px-4 py-2.5 text-neutral-700" title={a.category_name}>{a.category_name || "—"}</td>
                      <td className="max-w-[180px] truncate px-4 py-2.5 text-neutral-700" title={a.org_name}>{a.org_name || "—"}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-neutral-500">{a.barcode || "—"}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-neutral-500">{a.serial_number || "—"}</td>
                      <td className="px-4 py-2.5 text-center"><CondReportCell hasReport={a.has_condition_report} hasValues={a.has_condition_values} testid={`client-asset-cond-${a.asset_id}`} /></td>
                      <td className="px-4 py-2.5 text-right"><ArrowSquareOut size={15} className="ml-auto text-neutral-300" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <PageAdvice items={[
          "This page is cached in the Hub database. Use \u201cRefresh from Aroflo\u201d to pull the very latest client, task, quote and invoice data.",
          "Each feed shows up to the 50 most-recent records \u2014 older items live in Aroflo and the full data export.",
          "Tasks and quotes here link straight to their cached detail pages; open one to drill in.",
        ]} />
      </div>
    </Layout>
  );
}
